import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import Reveal from '../components/ui/Reveal';
import API, { UPLOADS_URL } from '../api/axios';

const resolveImage = (src) => {
  if (!src) return '';
  if (src.startsWith('http') || src.startsWith('/')) return src;
  return `${UPLOADS_URL}/${src}`;
};

const paras = (text) => (text || '').split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);

const DEFAULT_SECTIONS = [
  {
    section_key: 'hero',
    section_type: 'hero',
    image: '/images/page-banners/curricullam-infrastructure.jpg',
  },
  {
    section_key: 'intro',
    section_type: 'intro',
    kicker: 'Our Infrastructure',
    title: 'A Campus Designed for Every Kind of Growth',
    image: '/images/facilities/Infrastructure.jpg',
    description:
      'St. Andrews World School, Indirapuram offers a thoughtfully designed campus that balances academic spaces, recreational areas and creative zones.\n\nEvery facility has been planned to support the physical, intellectual and artistic growth of our students \u2014 while ensuring safety, hygiene and comfort throughout the school.',
  },
  {
    section_key: 'digital_classrooms',
    section_type: 'card',
    image: '/images/facilities/Junior-Classroom.jpg',
    icon: 'fa-chalkboard-user',
    title: 'Modern Digital Classrooms',
    description: 'Spacious, well-ventilated classrooms equipped with smart displays, high-speed connectivity and comfortable furniture for engaging, multimedia-based learning.',
  },
  {
    section_key: 'outer_play_area',
    section_type: 'card',
    image: '/images/coactivities/Football.jpg',
    icon: 'fa-futbol',
    title: 'Outer Play Area',
    description: 'A dedicated open playground for physical activities, games and free play \u2014 promoting fitness, teamwork and healthy social interaction in a safe environment.',
  },
  {
    section_key: 'music_room',
    section_type: 'card',
    image: '/images/facilities/Music-Room.jpg',
    icon: 'fa-music',
    title: 'Music Room',
    description: 'A well-equipped room where students learn, practise and explore a variety of instruments under the guidance of trained instructors.',
  },
  {
    section_key: 'dance_room',
    section_type: 'card',
    image: '/images/coactivities/curriculum-2.jpg',
    icon: 'fa-person-walking-dotted-line',
    title: 'Dance Room',
    description: 'A dedicated studio with proper flooring, mirrors and sound arrangements that encourages self-expression, discipline and stage confidence.',
  },
  {
    section_key: 'activity_hall',
    section_type: 'card',
    image: '/images/coactivities/curriculum-1.jpg',
    icon: 'fa-people-group',
    title: 'Activity Hall',
    description: 'A versatile space for workshops, competitions, indoor games and skill-development sessions that complement the academic curriculum.',
  },
  {
    section_key: 'staff_room',
    section_type: 'card',
    image: '/images/coactivities/curriculum3.jpg',
    icon: 'fa-print',
    title: 'Staff Room',
    description: 'A comfortable space with dedicated seating where teachers meet, plan lessons and hold collaborative discussions for student progress.',
  },
  {
    section_key: 'conference_hall',
    section_type: 'card',
    image: '/images/coactivities/curriculum4.jpg',
    icon: 'fa-video',
    title: 'Conference Hall',
    description: 'Acoustically equipped with audio-visual technology for official meetings, parent interactions, presentations and smooth communication.',
  },
  {
    section_key: 'cta',
    section_type: 'cta',
    icon: 'fa-building-columns',
    title: 'Built for Learning. Designed for Life.',
    description: 'Experience the campus of St. Andrews World School, Indirapuram for yourself.',
    button_text: 'Enquire for Admission',
    button_url: '/admission',
    button2_text: 'View Gallery',
    button2_url: '/gallery',
  },
];

const InfrastructurePage = () => {
  const title = 'Infrastructure';
  const [sections, setSections] = useState(DEFAULT_SECTIONS);

  useEffect(() => {
    let mounted = true;
    const fetchSections = async () => {
      try {
        const res = await API.get('/infrastructure/sections');
        if (!mounted) return;
        if (res.data?.data?.length) setSections(res.data.data);
      } catch (err) {
        /* fall back to defaults */
      }
    };
    fetchSections();
    return () => { mounted = false; };
  }, []);

  const hero = sections.find((s) => s.section_type === 'hero');
  const intro = sections.find((s) => s.section_type === 'intro');
  const cards = sections.filter((s) => s.section_type === 'card');
  const cta = sections.find((s) => s.section_type === 'cta');
  const introParas = paras(intro && intro.description);

  return (
    <>
      <SEO
        slug="infrastructure"
        title={title}
        description="Infrastructure of St. Andrews World School, Indirapuram — smart classrooms, play areas, music and dance rooms, activity hall, staff room, conference hall and auditorium."
        keywords="Infrastructure, SAWS Indirapuram, school facilities"
      />

      <section className="page-hero">
        <img
          src={resolveImage(hero && hero.image)}
          alt={intro && intro.title ? intro.title : title}
          className="page-hero-img"
          loading="eager"
        />
        <div className="page-hero-content">
          <div className="container">
            <div className="page-hero-inner" />
          </div>
        </div>
      </section>

      {/* INTRO */}
      <section className="py-16 md:py-20 bg-white relative overflow-hidden">
        <div className="absolute top-24 -right-24 w-96 h-96 rounded-full bg-[#1B3160]/5 blur-3xl" aria-hidden />
        <div className="absolute bottom-0 -left-32 w-80 h-80 rounded-full bg-[#D4A73E]/10 blur-3xl" aria-hidden />
        <div className="container relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 md:gap-16 items-center">
            <Reveal className="relative max-lg:order-2">
              <div className="absolute -inset-1 bg-gradient-to-br from-[#1B3160]/30 to-[#D4A73E]/20 rounded-3xl blur-lg opacity-60" aria-hidden />
              <img
                src={resolveImage(intro && intro.image)}
                alt={intro && intro.title ? intro.title : 'Campus at St. Andrews World School, Indirapuram'}
                className="relative w-full aspect-[4/3] object-cover rounded-2xl shadow-2xl"
                loading="lazy"
              />
            </Reveal>
            <div className="max-lg:order-1">
              <Reveal>
                <div className="kicker">{intro && (intro.kicker || 'Our Infrastructure')}</div>
                <h2 className="text-[30px] md:text-[38px] font-bold text-primary mt-2 leading-tight">
                  {(intro && intro.title) || 'A Campus Designed for Every Kind of Growth'}
                </h2>
                <div className="w-14 h-[3px] bg-[#D4A73E] rounded-full my-6"></div>
              </Reveal>
              <div className="space-y-5">
                {introParas.map((para, index) => (
                  <Reveal key={index} delayMs={100 + index * 60}>
                    <p className="text-body text-[16px] md:text-[17px] leading-relaxed">{para}</p>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FACILITIES GRID */}
      <section className="py-16 md:py-20 bg-light">
        <div className="container">
          <Reveal>
            <div className="text-center max-w-3xl mx-auto mb-12">
              <div className="kicker">What Our Campus Offers</div>
              <h2 className="text-[26px] md:text-[32px] font-bold text-primary mt-2 leading-tight">
                Facilities That Support Every Learner
              </h2>
              <div className="w-14 h-[3px] bg-[#D4A73E] rounded-full mx-auto mt-4"></div>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {cards.map((item, index) => (
              <Reveal key={item.section_key || item.title || index} delayMs={index * 60}>
                <article className="group bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden transition-all duration-300 hover:shadow-xl hover:-translate-y-1 h-full flex flex-col md:flex-row">
                  <div className="relative overflow-hidden md:w-[40%] shrink-0">
                    <img
                      src={resolveImage(item.image)}
                      alt={`${item.title} at St. Andrews World School, Indirapuram`}
                      className="w-full h-full min-h-[180px] object-cover transition-transform duration-700 group-hover:scale-[1.06]"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-6 flex-1">
                    <div className="flex items-center gap-3">
                      <div className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary group-hover:bg-primary group-hover:text-white transition-all duration-300">
                        <i className={`fas ${item.icon}`} aria-hidden />
                      </div>
                      <h3 className="text-primary font-bold text-lg leading-snug">{item.title}</h3>
                    </div>
                    <p className="text-body text-[14px] leading-relaxed mt-3">{item.description}</p>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-20 bg-[#1B3260] relative overflow-hidden text-white">
        <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, #fff 1px, transparent 0)', backgroundSize: '24px 24px' }} aria-hidden />
        <div className="container relative">
          <Reveal>
            <div className="text-center max-w-3xl mx-auto">
              <i className={`fas ${(cta && cta.icon) || 'fa-building-columns'} mx-auto mb-6 text-4xl text-[#F3D27E]`} aria-hidden />
              <h2 className="text-[26px] md:text-[34px] font-bold text-white leading-tight">
                {(cta && cta.title) || 'Built for Learning. Designed for Life.'}
              </h2>
              <p className="text-white/70 text-[16px] md:text-[17px] leading-relaxed mt-4">
                {(cta && cta.description) || 'Experience the campus of St. Andrews World School, Indirapuram for yourself.'}
              </p>
              <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row sm:flex-wrap">
                <Link to={(cta && cta.button_url) || '/admission'} className="btn-gold">
                  {(cta && cta.button_text) || 'Enquire for Admission'}
                  <i className="fas fa-arrow-right text-[12px]" aria-hidden />
                </Link>
                <Link
                  to={(cta && cta.button2_url) || '/gallery'}
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full text-sm font-semibold border border-white/30 text-white bg-white/10 backdrop-blur hover:bg-white/15 hover:border-white/40 transition-all duration-300"
                >
                  {(cta && cta.button2_text) || 'View Gallery'}
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
};

export default InfrastructurePage;